/**
 * REQUIRED PARKING RULES (2026-10-19, Matt, database/313): how many spaces a home needs, by the
 * size of the home — the rule G001's Parking row reads against the model's measured area. Set on
 * a JURISDICTION (the city's rule) and, only where a zone or overlay changes it, on the zone —
 * the parking-stall-size precedent: overlay → zone → jurisdiction. One rule per record.
 * A rule drafted by Claude is "Not confirmed" until a person confirms it; editing the numbers
 * takes the confirmation back off. See docs/DEVELOPMENT_STANDARDS.md.
 *
 * Props: kind 'jurisdiction' | 'zone', sourceId (Airtable record id), sourceName, byName.
 */
import { useEffect, useState } from 'react'
import { listParkingRules, saveParkingRule, confirmParkingRule, deleteParkingRule } from '../services/developmentStandards.js'
import { alertDialog, confirmDialog } from './Dialog.jsx'

const FONT = 'DM Sans, sans-serif'
const MONO = 'DM Mono, monospace'
const AREA_BASIS = [
  { value: 'living', label: 'Living area' },
  { value: 'living_storage', label: 'Living + mechanical/storage area' },
]
const input = { fontFamily: MONO, fontSize: 12, padding: '4px 6px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', width: 70 }
const btn = (primary) => ({ fontFamily: FONT, fontSize: 12, padding: '4px 12px', borderRadius: 20, cursor: 'pointer', border: '0.5px solid #07272D', background: primary ? '#07272D' : 'transparent', color: primary ? '#fff' : 'var(--aj-text-mid)' })

function describe(p) {
  const sp = (c, u) => [c ? `${c} covered` : null, u ? `${u} uncovered` : null].filter(Boolean).join(' + ') || 'none'
  const basis = AREA_BASIS.find((a) => a.value === p.area_basis)?.label.toLowerCase() || 'living area'
  const base = `${sp(p.covered, p.uncovered)}`
  if (p.larger_over_sf == null) return `${base} per home.`
  return `${base} under ${Number(p.larger_over_sf).toLocaleString('en-US')} SF of ${basis}; ${sp(p.larger_covered, p.larger_uncovered)} at or above it.`
}

export default function ParkingRulesPanel({ kind, sourceId, sourceName, byName }) {
  const [rules, setRules] = useState(null)
  const [draft, setDraft] = useState(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    setRules(null); setDraft(null)
    if (!sourceId) return
    listParkingRules(kind, sourceId).then(setRules).catch(() => setRules([]))
  }, [kind, sourceId])

  const rule = rules?.[0] || null
  const startEdit = () => setDraft(rule ? { ...rule, params: { ...rule.params } } : {
    label: 'Single-unit dwelling', citation: '', notes: '',
    params: { covered: 2, uncovered: 0, larger_over_sf: '', larger_covered: '', larger_uncovered: '', area_basis: 'living' },
  })
  const setP = (k, v) => setDraft((d) => ({ ...d, params: { ...d.params, [k]: v } }))

  async function save() {
    setBusy(true)
    try {
      const row = await saveParkingRule({ ...draft, source_kind: kind, source_airtable_id: sourceId, source_name: sourceName, confirmed: false }, byName)
      setRules([row]); setDraft(null)
    } catch (err) {
      await alertDialog({ title: 'Not saved', message: err.message || 'The rule could not be saved.' })
    }
    setBusy(false)
  }
  async function toggle() {
    setBusy(true)
    try { const row = await confirmParkingRule(rule.id, !rule.confirmed, byName); setRules([row]) }
    catch (err) { await alertDialog({ title: 'Not saved', message: err.message || 'The rule could not be updated.' }) }
    setBusy(false)
  }
  async function remove() {
    if (!(await confirmDialog({ title: 'Delete parking rule?', message: kind === 'zone' ? 'This zone will use its jurisdiction’s rule.' : 'Projects in this jurisdiction go back to the required parking typed on each parcel.', confirmLabel: 'Delete', danger: true }))) return
    setBusy(true)
    try { await deleteParkingRule(rule.id); setRules([]) } catch (err) { await alertDialog({ title: 'Not deleted', message: err.message || '' }) }
    setBusy(false)
  }

  const num = (k, w) => (
    <input style={{ ...input, width: w || 70 }} inputMode="numeric" value={draft.params[k] ?? ''} onChange={(e) => setP(k, e.target.value)} />
  )
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 4 }}>{kind === 'zone' ? 'Required parking override' : 'Required parking'}</div>
      <div style={{ fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 10, lineHeight: 1.45 }}>
        {kind === 'zone'
          ? 'Only when this zone or overlay changes the jurisdiction’s parking count.'
          : 'How many spaces a home needs. The title sheet reads it against the area AlderCAD measures, so a larger home gets the larger count without anyone retyping it.'}
      </div>
      {rules === null ? (
        <div style={{ fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading…</div>
      ) : draft ? (
        <div style={{ border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: 12, background: 'var(--aj-bg-surface)', fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-mid)', display: 'grid', gap: 8 }}>
          <div>Covered {num('covered', 50)} · uncovered {num('uncovered', 50)} per home</div>
          <div>At or above {num('larger_over_sf', 80)} SF of{' '}
            <select value={draft.params.area_basis || 'living'} onChange={(e) => setP('area_basis', e.target.value)} style={{ ...input, width: 'auto', fontFamily: FONT }}>
              {AREA_BASIS.map((a) => <option key={a.value} value={a.value}>{a.label.toLowerCase()}</option>)}
            </select>
            : covered {num('larger_covered', 50)} · uncovered {num('larger_uncovered', 50)}
          </div>
          <div style={{ color: 'var(--aj-text-muted)' }}>Leave the size blank when the count doesn&apos;t change with size.</div>
          <input style={{ ...input, width: '100%', fontFamily: FONT }} placeholder="Code citation" value={draft.citation || ''} onChange={(e) => setDraft((d) => ({ ...d, citation: e.target.value }))} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn(true)} disabled={busy} onClick={save}>Save rule</button>
            <button style={btn(false)} disabled={busy} onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      ) : rule ? (
        <div style={{ border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: 12, background: 'var(--aj-bg-surface)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, fontFamily: FONT, fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>{rule.label || 'Required parking'}</div>
            <span style={{ fontFamily: FONT, fontSize: 11, padding: '2px 8px', borderRadius: 20, background: rule.confirmed ? 'rgba(46,125,50,0.12)' : 'rgba(184,100,10,0.14)', color: rule.confirmed ? '#2e7d32' : '#b8640a' }}>
              {rule.confirmed ? 'Confirmed' : 'Not confirmed'}
            </span>
            <button style={btn(!rule.confirmed)} disabled={busy} onClick={toggle}>{rule.confirmed ? 'Withdraw' : 'Confirm'}</button>
            <button style={btn(false)} disabled={busy} onClick={startEdit}>Edit</button>
            <button style={{ ...btn(false), borderColor: 'var(--aj-border-mid)' }} disabled={busy} onClick={remove}>Delete</button>
          </div>
          <div style={{ fontFamily: FONT, fontSize: 12.5, color: 'var(--aj-text-mid)', marginTop: 6, lineHeight: 1.45 }}>{describe(rule.params || {})}</div>
          {rule.notes ? <div style={{ fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 4 }}>{rule.notes}</div> : null}
          {rule.citation ? <div style={{ fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 6 }}>{rule.citation}</div> : null}
        </div>
      ) : (
        <button style={btn(false)} onClick={startEdit}>Add a parking rule</button>
      )}
    </div>
  )
}
